import { useRef, useEffect } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html, Line } from "@react-three/drei";
import * as THREE from "three";

// Colours by call-frame state.
const COLOR_ACTIVE = "#fbbf24"; // currently executing frame
const COLOR_STACK = "#60a5fa"; // ancestor frame on the call stack
const COLOR_DONE = "#22c55e"; // returned (subarray sorted)
const COLOR_EDGE = "rgba(255,255,255,0.18)";
const Z_STEP = 1.3; // depth staggering → a volumetric tree, not a flat plane

// Lay the recursion tree out: x by the subarray's centre (ranges partition, so
// siblings never overlap), y by depth (root on top), z stepped back per level.
function computeLayout(nodes, n) {
  const spreadX = Math.max(9, n * 1.7);
  const levelY = 2.6;
  const maxDepth = nodes.reduce((m, nd) => Math.max(m, nd.depth), 0);
  const pos = {};
  nodes.forEach((nd) => {
    const center = (nd.lo + nd.hi) / 2;
    const x = n > 1 ? (center / (n - 1) - 0.5) * spreadX : 0;
    const y = (maxDepth / 2 - nd.depth) * levelY;
    const z = -nd.depth * Z_STEP;
    pos[nd.id] = [x, y, z];
  });
  return { pos, maxDepth, spreadX };
}

function colorFor(node, step) {
  if (step.activeId === node.id) return COLOR_ACTIVE;
  if (step.stackIds.includes(node.id)) return COLOR_STACK;
  return COLOR_DONE; // revealed but not on the stack → returned/sorted
}

function TreeNode({ position, color, active, values, dragHandlers }) {
  return (
    <group position={position}>
      <mesh scale={active ? 1.25 : 1} {...(dragHandlers || {})}>
        <sphereGeometry args={[0.55, 28, 28]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={active ? 0.7 : 0.15}
          roughness={0.35}
          metalness={0.1}
        />
      </mesh>
      <Html center distanceFactor={14} pointerEvents="none" zIndexRange={[100, 0]}>
        <div className={`ms3d-node-label ${active ? "active" : ""}`}>{values}</div>
      </Html>
    </group>
  );
}

// Smoothly pans the camera to keep the active node in view — unless the user
// has taken manual control by dragging the tree (manualRef).
function CameraRig({ target, manualRef }) {
  const controls = useThree((s) => s.controls);
  const camera = useThree((s) => s.camera);
  const desired = useRef(new THREE.Vector3());
  useFrame(() => {
    if (manualRef.current || !controls || !target) return;
    desired.current.set(target[0], target[1], target[2]);
    const delta = desired.current.clone().sub(controls.target);
    if (delta.lengthSq() < 0.0004) return;
    const nudge = delta.multiplyScalar(0.05);
    controls.target.add(nudge);
    camera.position.add(nudge);
    controls.update();
  });
  return null;
}

// Everything inside the Canvas (so it can use useThree for the drag maths).
function SceneContents({ nodes, step, n }) {
  const { pos, spreadX } = computeLayout(nodes, n);
  const revealed = new Set(step.revealedIds);
  const activePos = pos[step.activeId] || [0, 0, 0];
  const rootId = nodes.find((nd) => nd.parent === null)?.id;

  const controls = useThree((s) => s.controls);
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);

  const groupRef = useRef();
  const manualRef = useRef(false); // true once the user drags the tree
  const drag = useRef({
    active: false,
    plane: new THREE.Plane(),
    raycaster: new THREE.Raycaster(),
    start: new THREE.Vector3(),
    startPos: new THREE.Vector3(),
    tmp: new THREE.Vector3(),
  });

  // Reset position + re-enable follow whenever the tree changes (new array).
  useEffect(() => {
    if (groupRef.current) groupRef.current.position.set(0, 0, 0);
    manualRef.current = false;
  }, [nodes]);

  // Project a screen point onto the current drag plane → world point.
  const projectToPlane = (clientX, clientY, out) => {
    const rect = gl.domElement.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
    drag.current.raycaster.setFromCamera({ x: ndcX, y: ndcY }, camera);
    return drag.current.raycaster.ray.intersectPlane(drag.current.plane, out);
  };

  const onWindowMove = (ev) => {
    if (!drag.current.active || !groupRef.current) return;
    const hit = projectToPlane(ev.clientX, ev.clientY, drag.current.tmp);
    if (hit) {
      groupRef.current.position.copy(drag.current.startPos).add(drag.current.tmp.clone().sub(drag.current.start));
    }
  };

  const onWindowUp = () => {
    drag.current.active = false;
    if (controls) controls.enabled = true;
    document.body.style.cursor = "";
    window.removeEventListener("pointermove", onWindowMove);
    window.removeEventListener("pointerup", onWindowUp);
  };

  const onRootPointerDown = (e) => {
    e.stopPropagation();
    manualRef.current = true; // manual placement takes over from auto-follow
    drag.current.active = true;
    if (controls) controls.enabled = false; // don't orbit while dragging the tree
    document.body.style.cursor = "grabbing";

    // Drag plane faces the camera, through the tree's current origin.
    const normal = camera.getWorldDirection(new THREE.Vector3()).negate();
    drag.current.plane.setFromNormalAndCoplanarPoint(normal, groupRef.current.position.clone());
    projectToPlane(e.clientX, e.clientY, drag.current.start);
    drag.current.startPos.copy(groupRef.current.position);

    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", onWindowUp);
  };

  // Clean up listeners if unmounted mid-drag.
  useEffect(() => () => {
    window.removeEventListener("pointermove", onWindowMove);
    window.removeEventListener("pointerup", onWindowUp);
  }, []);

  const rootHandlers = {
    onPointerDown: onRootPointerDown,
    onPointerOver: () => {
      if (!drag.current.active) document.body.style.cursor = "grab";
    },
    onPointerOut: () => {
      if (!drag.current.active) document.body.style.cursor = "";
    },
  };

  return (
    <>
      <color attach="background" args={["#0a1424"]} />
      <ambientLight intensity={0.6} />
      <directionalLight position={[6, 10, 8]} intensity={0.8} />
      <pointLight position={[-8, -6, 6]} intensity={0.4} color="#60a5fa" />

      <group ref={groupRef}>
        {/* Edges: parent → child, only when both are revealed */}
        {nodes.map((nd) => {
          if (nd.parent === null || !revealed.has(nd.id) || !revealed.has(nd.parent)) return null;
          return (
            <Line key={`e-${nd.id}`} points={[pos[nd.parent], pos[nd.id]]} color={COLOR_EDGE} lineWidth={1.5} />
          );
        })}

        {/* Nodes — the root node carries the drag handlers */}
        {nodes.map((nd) => {
          if (!revealed.has(nd.id)) return null;
          const active = step.activeId === nd.id;
          const values = step.array.slice(nd.lo, nd.hi + 1).join(", ");
          return (
            <TreeNode
              key={`n-${nd.id}`}
              position={pos[nd.id]}
              color={colorFor(nd, step)}
              active={active}
              values={values}
              dragHandlers={nd.id === rootId ? rootHandlers : null}
            />
          );
        })}
      </group>

      <CameraRig target={activePos} manualRef={manualRef} />
      <OrbitControls enablePan enableZoom enableRotate makeDefault />
    </>
  );
}

function RecursionTree3D({ nodes, step, n }) {
  const spreadX = Math.max(9, n * 1.7);
  const camZ = Math.max(14, spreadX * 1.0);
  return (
    <Canvas camera={{ position: [0, 0, camZ], fov: 45 }} dpr={[1, 2]}>
      <SceneContents nodes={nodes} step={step} n={n} />
    </Canvas>
  );
}

export default RecursionTree3D;
